import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface CompanyDetails {
    companyName: string;
    website: string;
industry: string;
    stage: string;
    slideCount: string;
}

interface PromptDetails {
    prompt: string;
    fileNames: string[];
    userRequestedDeepResearch: boolean;
    templateId?: string;
    templateName?: string;
    templateThumbnail?: string;
    templateCategory?: string;
}

interface QAAnswer {
    text: string;
    source: string;
    confidence: number;
    references: string[];
}

interface QAQuestion {
    id: string;
    section: string;
    sectionTitle?: string;
    field: string;
    question: string;
    answer: QAAnswer;
    status: 'answered' | 'pending';
    isAnswered: boolean;
}

interface QAStats {
    totalFields: number;
    answered: number;
    unanswered: number;
    completionPercentage: number;
}

interface QASession {
    sessionId: string | null;
    stage: string | null;
    stats: QAStats | null;
    questions: QAQuestion[];
    sections: Record<string, { title: string; status: 'pending' | 'loading' | 'completed' }>;
    applicableSections: string[];
    isLoading: boolean;
    error: string | null;
}

interface StreamingSlide {
    id: string;
    title: string;
    htmlContent: string;
    order: number;
    slideType?: string;
    sectionId?: string;
}

interface SlideGeneration {
    isGenerating: boolean;
    totalSlides: number;
    completedSlides: number;
    totalBatches: number;
    completedBatches: number;
    streamingSlides: StreamingSlide[];
}

interface CreateDeckState {
    currentStep: number;
    stepName: string;
    deckType: string;
    projectId: string | null;
    companyDetails: CompanyDetails;
    promptDetails: PromptDetails;
    qaSession: QASession;
    interactiveQA: Record<string, string>;
    slideGeneration: SlideGeneration;
    researchReport: string | null;
}

const initialState: CreateDeckState = {
    currentStep: 1,
    stepName: 'company-details',
    deckType: 'fundraising-pitch-deck', // Default or empty
    projectId: null,
    companyDetails: {
        companyName: '',
        website: '',
        industry: '',
        stage: '',
        slideCount: 'Auto',
    },
    promptDetails: {
        prompt: '',
        fileNames: [],
        userRequestedDeepResearch: false,
    },
    qaSession: {
        sessionId: null,
        stage: null,
        stats: null,
        questions: [],
        sections: {},
        applicableSections: [],
        isLoading: false,
        error: null,
    },
    interactiveQA: {},
    slideGeneration: {
        isGenerating: false,
        totalSlides: 0,
        completedSlides: 0,
        totalBatches: 0,
        completedBatches: 0,
        streamingSlides: [],
    },
    researchReport: null,
};

const createDeckSlice = createSlice({
    name: 'createDeck',
    initialState,
    reducers: {
        setStep: (state, action: PayloadAction<{ step: number; name: string }>) => {
            state.currentStep = action.payload.step;
            state.stepName = action.payload.name;
        },
        setDeckType: (state, action: PayloadAction<string>) => {
            state.deckType = action.payload;
        },
        setProjectId: (state, action: PayloadAction<string | null>) => {
            state.projectId = action.payload;
        },
        setCompanyDetails: (state, action: PayloadAction<CompanyDetails>) => {
            state.companyDetails = action.payload;
        },
        setPromptDetails: (state, action: PayloadAction<PromptDetails>) => {
            state.promptDetails = action.payload;
        },
        setInteractiveQAAnswer: (state, action: PayloadAction<{ questionId: string; answer: string }>) => {
            state.interactiveQA[action.payload.questionId] = action.payload.answer;
        },
        setQASession: (state, action: PayloadAction<{
            sessionId: string;
            stage: string;
            stats: QAStats;
            questions: QAQuestion[];
            applicableSections: string[];
        }>) => {
            state.qaSession = {
                ...action.payload,
                sections: action.payload.applicableSections.reduce((acc, curr) => ({
                    ...acc,
                    [curr]: {
                        title: camelCaseToReadable(curr),
                        status: 'completed'
                    }
                }), {}),
                isLoading: false,
                error: null,
            };
        },
        setQALoading: (state, action: PayloadAction<boolean>) => {
            state.qaSession.isLoading = action.payload;
        },
        setQAError: (state, action: PayloadAction<string>) => {
            state.qaSession.error = action.payload;
            state.qaSession.isLoading = false;
        },
        updateQAQuestionAnswer: (state, action: PayloadAction<{ questionId: string; answer: string }>) => {
            let question = state.qaSession.questions.find(q => q.id === action.payload.questionId);

            // If question doesn't exist (synthetic question), create it
            if (!question && action.payload.questionId === 'synthetic-team-question') {
                const newQuestion: QAQuestion = {
                    id: 'synthetic-team-question',
                    section: 'team',
                    sectionTitle: 'Team',
                    field: 'foundingTeam',
                    question: 'Tell us about your founding team',
                    answer: { text: action.payload.answer, source: 'user', confidence: 0, references: [] },
                    status: 'answered',
                    isAnswered: true
                };
                state.qaSession.questions.push(newQuestion);
                question = newQuestion;
            }

            if (question) {
                question.answer.text = action.payload.answer;

                // Check if this is a JSON answer (fundraising or team data)
                let hasContent = action.payload.answer.trim().length > 0;
                const trimmedAnswer = action.payload.answer.trim();
                if (hasContent && (trimmedAnswer.startsWith('{') || trimmedAnswer.startsWith('['))) {
                    try {
                        const parsed = JSON.parse(action.payload.answer);

                        // For fundraising data (object with roundType)
                        if (parsed.roundType !== undefined) {
                            const hasCurrentRound = parsed.currentRound?.targetAmount?.trim?.()?.length > 0;
                            const hasDebtFinancing = parsed.debtFinancing?.loanAmount?.trim?.()?.length > 0;
                            // For "raised_before", also check if previous rounds have data
                            const hasPreviousRounds = parsed.roundType === 'raised_before' &&
                                parsed.previousRounds?.some((r: any) =>
                                    r.amountRaised?.trim?.()?.length > 0 ||
                                    r.valuation?.trim?.()?.length > 0
                                );
                            hasContent = hasCurrentRound || hasDebtFinancing || hasPreviousRounds;
                        }
                        // For team data - direct array format [{ id, name, role, bio }]
                        // Require at least one member with ALL fields filled (name, role, bio)
                        else if (Array.isArray(parsed)) {
                            hasContent = parsed.some((m: any) =>
                                m.name?.trim?.()?.length > 0 &&
                                m.role?.trim?.()?.length > 0 &&
                                m.bio?.trim?.()?.length > 0
                            );
                        }
                        // For team data - wrapped format { members: [...] }
                        // Require at least one member with ALL fields filled (name, role, bio)
                        else if (parsed.members !== undefined) {
                            hasContent = parsed.members.some((m: any) =>
                                m.name?.trim?.()?.length > 0 &&
                                m.role?.trim?.()?.length > 0 &&
                                m.bio?.trim?.()?.length > 0
                            );
                        }
                    } catch {
                        // Not valid JSON, use default check
                    }
                }

                question.isAnswered = hasContent;
                question.status = question.isAnswered ? 'answered' : 'pending';
            }
        },
        // Streaming Actions
        initStreamSession: (state, action: PayloadAction<{ sessionId: string; stage: string; totalSections: number; sections: string[] }>) => {
            state.qaSession = {
                sessionId: action.payload.sessionId,
                stage: action.payload.stage,
                stats: { totalFields: 0, answered: 0, unanswered: 0, completionPercentage: 0 },
                questions: [],
                sections: action.payload.sections.reduce((acc, curr) => ({
                    ...acc,
                    [curr]: { title: camelCaseToReadable(curr), status: 'pending' }
                }), {}),
                applicableSections: action.payload.sections,
                isLoading: true,
                error: null,
            };
        },
        addSectionPlaceholder: (state, action: PayloadAction<{ sectionIndex: number; sectionName: string; sectionTitle: string }>) => {
            if (state.qaSession.sections[action.payload.sectionName]) {
                state.qaSession.sections[action.payload.sectionName] = {
                    title: action.payload.sectionTitle,
                    status: 'loading'
                };
            }
        },
        updateSectionWithQuestions: (state, action: PayloadAction<{ sectionName: string; questions: QAQuestion[] }>) => {
            // Update section status
            if (state.qaSession.sections[action.payload.sectionName]) {
                state.qaSession.sections[action.payload.sectionName].status = 'completed';
            }

            // Add new questions, avoiding duplicates
            const newQuestions = action.payload.questions.filter(
                newQ => !state.qaSession.questions.some(existingQ => existingQ.id === newQ.id)
            );
            state.qaSession.questions.push(...newQuestions);

            // Update stats
            const stats = state.qaSession.stats || { totalFields: 0, answered: 0, unanswered: 0, completionPercentage: 0 };
            stats.totalFields += newQuestions.length;
            stats.answered += newQuestions.filter(q => q.isAnswered).length;
            stats.unanswered = stats.totalFields - stats.answered;
            stats.completionPercentage = stats.totalFields > 0 ? Math.round((stats.answered / stats.totalFields) * 100) : 0;
            state.qaSession.stats = stats;
        },
        completeStream: (state, action: PayloadAction<{ stats: QAStats }>) => {
            state.qaSession.isLoading = false;
            state.qaSession.stats = action.payload.stats;
        },
        clearQASession: (state) => {
            state.qaSession = {
                sessionId: null,
                stage: null,
                stats: null,
                questions: [],
                sections: {},
                applicableSections: [],
                isLoading: false,
                error: null,
            };
        },
        // Slide Generation Actions
        setSlideGenerationStart: (state, action: PayloadAction<{ totalSlides: number; totalBatches: number }>) => {
            state.slideGeneration = {
                isGenerating: true,
                totalSlides: action.payload.totalSlides,
                completedSlides: 0,
                totalBatches: action.payload.totalBatches,
                completedBatches: 0,
                streamingSlides: [],
            };
        },
        addStreamingSlide: (state, action: PayloadAction<StreamingSlide>) => {
            // Avoid duplicates
            const exists = state.slideGeneration.streamingSlides.some(s => s.id === action.payload.id);
            if (!exists) {
                state.slideGeneration.streamingSlides.push(action.payload);
                // Keep slides sorted by order
                state.slideGeneration.streamingSlides.sort((a, b) => a.order - b.order);
            }
        },
        clearStreamingSlides: (state) => {
            state.slideGeneration.streamingSlides = [];
        },
        setSlideGenerationProgress: (state, action: PayloadAction<{ completedSlides: number; completedBatches: number }>) => {
            state.slideGeneration.completedSlides = action.payload.completedSlides;
            state.slideGeneration.completedBatches = action.payload.completedBatches;
        },
        setSlideGenerationComplete: (state) => {
            state.slideGeneration.isGenerating = false;
        },
        setResearchReport: (state, action: PayloadAction<string>) => {
            state.researchReport = action.payload;
        },
        clearResearchReport: (state) => {
            state.researchReport = null;
        },
        resetCreateDeck: () => {
            return initialState;
        },
    },
});

export const {
    setStep,
    setDeckType,
    setProjectId,
    setCompanyDetails,
    setPromptDetails,
    setInteractiveQAAnswer,
    setQASession,
    setQALoading,
    setQAError,
    updateQAQuestionAnswer,
    clearQASession,
    resetCreateDeck,
    initStreamSession,
    addSectionPlaceholder,
    updateSectionWithQuestions,
    completeStream,
    // Slide Generation
    setSlideGenerationStart,
    setSlideGenerationProgress,
    setSlideGenerationComplete,
    addStreamingSlide,
    clearStreamingSlides,
    // Research Report
    setResearchReport,
    clearResearchReport,
} = createDeckSlice.actions;

// Helper to convert camelCase to readable text (duplicated here to avoid circular dep if needed, or better moved to utils)
function camelCaseToReadable(str: string): string {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (char) => char.toUpperCase())
        .trim();
}

export default createDeckSlice.reducer;
